import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiShield, FiLock, FiEye, FiAlertTriangle, FiDatabase, FiMail, FiFileText, FiArrowRight } = FiIcons;

const DataLossPreventionPage = () => {
  const protectionAreas = [
    {
      icon: FiMail,
      title: 'Email Security',
      description: 'Prevent sensitive data leakage through email communications',
      features: [
        'Automatic email scanning and filtering',
        'Attachment security policies',
        'Data classification-based routing',
        'Encryption enforcement',
        'External sharing controls'
      ],
      stats: { blocked: '10,000+', prevented: '95%' }
    },
    {
      icon: FiFileText,
      title: 'Document Protection',
      description: 'Safeguard sensitive information in documents and files',
      features: [
        'Real-time document scanning',
        'Content-based policy enforcement',
        'Version control security',
        'Collaboration protection',
        'Document classification'
      ],
      stats: { protected: '1M+', scanned: '100%' }
    },
    {
      icon: FiDatabase,
      title: 'Database Security',
      description: 'Protect sensitive data stored in databases and data warehouses',
      features: [
        'Database activity monitoring',
        'SQL injection prevention',
        'Data access control enforcement',
        'Sensitive data masking',
        'Audit trail generation'
      ],
      stats: { monitored: '500+', secured: '99.9%' }
    },
    {
      icon: FiEye,
      title: 'Network Traffic Monitoring',
      description: 'Monitor and control data movement across network channels',
      features: [
        'Real-time traffic analysis',
        'Protocol-based filtering',
        'Bandwidth optimization',
        'Anomaly detection',
        'Geographic restrictions'
      ],
      stats: { analyzed: '1TB+', threats: '80%' }
    }
  ];

  const features = [
    { icon: FiShield, title: 'Advanced Threat Detection', description: 'AI-powered detection of potential data loss incidents and security threats' },
    { icon: FiLock, title: 'Encryption Management', description: 'Automated encryption of sensitive data based on classification and policies' },
    { icon: FiAlertTriangle, title: 'Real-time Alerts', description: 'Immediate notifications of policy violations and suspicious activities' },
    { icon: FiDatabase, title: 'Data Discovery', description: 'Automated discovery and classification of sensitive data across all systems' }
  ];

  const benefits = [
    { metric: '85%', description: 'Reduction in data breaches' },
    { metric: '70%', description: 'Faster incident response' },
    { metric: '90%', description: 'Improved data visibility' },
    { metric: '60%', description: 'Lower security costs' }
  ];

  return (
    <div className="py-16">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-red-900 to-orange-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-5xl font-bold mb-6">
                Advanced Data Loss Prevention
              </h1>
              <p className="text-xl text-red-100 mb-8 max-w-3xl mx-auto">
                Protect your sensitive data from unauthorized access and exfiltration with our comprehensive DLP solutions powered by Microsoft Purview.
              </p>
              <Link
                to="/purview-audit"
                className="inline-flex items-center bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg text-xl font-bold transition-all transform hover:scale-105"
              >
                Get Purview DLP Assessment
                <SafeIcon icon={FiArrowRight} className="ml-2" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Protect What Matters Most
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our DLP solutions deliver measurable improvements in data security and compliance
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-red-600 mb-2">{benefit.metric}</div>
                <div className="text-gray-600">{benefit.description}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Protection Areas Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Comprehensive Data Protection
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Multi-layered protection across all data access points and transmission channels
            </p>
          </div>
          <div className="grid lg:grid-cols-2 gap-8">
            {protectionAreas.map((area, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gray-50 p-8 rounded-lg shadow-lg"
              >
                <div className="flex items-start space-x-4 mb-6">
                  <div className="bg-red-100 p-3 rounded-lg">
                    <SafeIcon icon={area.icon} className="h-8 w-8 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{area.title}</h3>
                    <p className="text-gray-600">{area.description}</p>
                  </div>
                </div>
                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Key Features:</h4>
                  <ul className="space-y-2">
                    {area.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center">
                        <SafeIcon icon={FiShield} className="text-red-500 mr-3 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                  <div>
                    <div className="text-2xl font-bold text-red-600">{area.stats.blocked}</div>
                    <div className="text-sm text-gray-600">Threats Blocked</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-red-600">{area.stats.prevented}</div>
                    <div className="text-sm text-gray-600">Prevention Rate</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-red-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Advanced DLP Capabilities
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Cutting-edge features to prevent data loss and maintain security compliance
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-6 rounded-lg shadow-lg text-center hover:shadow-xl transition-shadow"
              >
                <SafeIcon icon={feature.icon} className="h-12 w-12 text-red-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-red-600 to-orange-600 text-white">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-6">
            Secure Your Sensitive Data
          </h2>
          <p className="text-xl mb-8 text-red-100">
            Don't wait for a data breach. Protect your organization with our comprehensive DLP solutions
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/purview-audit"
              className="inline-flex items-center bg-white text-red-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100 transition-colors"
            >
              Get Purview DLP Assessment
              <SafeIcon icon={FiArrowRight} className="ml-2" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg text-lg font-bold transition-colors"
            >
              Schedule Security Consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DataLossPreventionPage;